import { Label } from "@/components/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { ExperimentType } from "@/http/api-types";
import { Globe2, SlidersVertical } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import type { ExperimentFormValues } from "./ExperimentForm";
import { DEFAULT_CAMPAIGNS, DEFAULT_PREDICTION_FLIGHTS } from "./ExperimentForm";
import { EXPERIMENT_FORM_LABEL_CLASS } from "./experiment-form-styles";

type ExperimentTypeSelectorProps = {
  disabled: boolean;
};

export function ExperimentTypeSelector({ disabled }: ExperimentTypeSelectorProps) {
  const { control, setValue } = useFormContext<ExperimentFormValues>();
  const experimentType = useWatch({
    control,
    name: "experimentType",
  });

  const handleValueChange = (value: string) => {
    if (!value || disabled) return;

    const nextExperimentType = parseInt(value, 10) as ExperimentType;
    if (nextExperimentType === experimentType) return;

    setValue("experimentType", nextExperimentType, { shouldDirty: true });
    setValue("predictionFlights", [...DEFAULT_PREDICTION_FLIGHTS], { shouldDirty: true });
    setValue("importedGlobalExperimentId", "", { shouldDirty: true });
    setValue("importLastSyncedAt", "", { shouldDirty: true });
    setValue("scopeFilters", [], { shouldDirty: true });
    setValue("importMetadata", null, { shouldDirty: true });

    if (nextExperimentType === ExperimentType.Custom) {
      setValue("campaigns", [...DEFAULT_CAMPAIGNS], { shouldDirty: true });
    }
  };

  return (
    <section className="grid gap-3">
      <div>
        <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Experiment Type</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Global experiments import prediction-flight structure. Custom experiments keep the existing campaign flow.
        </p>
      </div>

      <ToggleGroup
        type="single"
        value={experimentType.toString()}
        onValueChange={handleValueChange}
        variant="outline"
        className="grid w-full grid-cols-1 items-stretch sm:grid-cols-2"
      >
        <ToggleGroupItem
          value={ExperimentType.Global.toString()}
          className="h-full min-h-[96px] whitespace-normal items-start justify-start self-stretch p-4"
          disabled={disabled}
        >
          <span className="flex h-full w-full min-w-0 items-start gap-3 text-left">
            <Globe2 className="mt-0.5 h-4 w-4" />
            <span className="grid min-w-0 gap-1">
              <span className="font-medium">Global</span>
              <span className="text-xs text-muted-foreground whitespace-normal break-words">
                Import source experiment structure, scope, prediction flights, and predictor configs.
              </span>
            </span>
          </span>
        </ToggleGroupItem>
        <ToggleGroupItem
          value={ExperimentType.Custom.toString()}
          className="h-full min-h-[96px] whitespace-normal items-start justify-start self-stretch p-4"
          disabled={disabled}
        >
          <span className="flex h-full w-full min-w-0 items-start gap-3 text-left">
            <SlidersVertical className="mt-0.5 h-4 w-4" />
            <span className="grid min-w-0 gap-1">
              <span className="font-medium">Custom</span>
              <span className="text-xs text-muted-foreground whitespace-normal break-words">
                Use the existing audience and campaign-based experiment setup.
              </span>
            </span>
          </span>
        </ToggleGroupItem>
      </ToggleGroup>
    </section>
  );
}
